import React from 'react';
import { Store } from '../../services/database/vision';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface PlanTableProps {
  title: string;
  stores: Store[];
  showValues: boolean;
  accentColor: string;
}

export const PlanTable: React.FC<PlanTableProps> = ({
  title,
  stores,
  showValues,
  accentColor
}) => {
  const formatCurrency = (value: number) => {
    if (!showValues) return "R$ •••••";
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTotalRevenue = () => {
    return stores.reduce((sum, store) => sum + (store.price || 0), 0);
  };

  const getTotalProfit = () => {
    return stores.reduce((sum, store) => {
      const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
      const totalCost = Object.values(costs).reduce((a, b) => a + b, 0);
      return sum + ((store.price || 0) - totalCost);
    }, 0);
  };

  return (
    <div className="h-full">
      <div className="bg-[#111]/90 rounded-[20px] border border-white/[0.08] h-full">
        {/* Header */}
        <div className="p-6 border-b border-white/[0.08]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-1 h-6 rounded-full ${accentColor}`} />
              <h3 className="text-lg font-medium">{title}</h3>
            </div>
            <p className="font-medium">{formatCurrency(getTotalRevenue())}</p>
          </div>
        </div>

        {/* Store List */}
        <div className="divide-y divide-white/[0.08]">
          {stores.map((store) => {
            const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
            const totalCost = Object.values(costs).reduce((a, b) => a + b, 0);
            const profit = (store.price || 0) - totalCost;
            
            return (
              <div 
                key={store.id} 
                className="px-6 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{store.name}</h4>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(store.price || 0)}</p>
                    </div>
                    <div className="text-right min-w-[100px]">
                      <div className="flex items-center gap-1 justify-end">
                        {profit >= 0 ? (
                          <TrendingUp size={16} className="text-green-500/70" />
                        ) : (
                          <TrendingDown size={16} className="text-red-500/70" />
                        )}
                        <span className={`${profit >= 0 ? 'text-green-500/70' : 'text-red-500/70'}`}>
                          {formatCurrency(profit)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.08] bg-white/[0.02]">
          <div className="flex justify-between items-center px-2">
            <p className="text-sm text-white/60">Total</p>
            <p className={`font-medium ${getTotalProfit() >= 0 ? 'text-green-500/70' : 'text-red-500/70'}`}>
              {formatCurrency(getTotalProfit())}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
